import { z } from 'zod';

import {
  ComplexRoleUI,
  DepartmentTaskType,
  DepartmentZone,
  ObjectForSelection
} from '../../types/interfaces';

import { EmployeeAssignmentScheduleSettingSchema } from './AssignmentScheduleSchema';
import { LocationSchema } from './LocationSchema';
import { ScheduleWithDatesSchema } from './ScheduleSchema';
import {
  validateCoordinates,
  validateCustomAddress,
  validateDateRangesOverlap,
  validateDestinationArea,
  validateFias
} from './validation-helpers';

const namesMessage = { message: 'До 40 символов' };

const RoleUISchema: z.ZodSchema<ComplexRoleUI> = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  roleUIs: z.array(z.any()).optional()
});

const TaskTypesSchema = z.object({
  id: z.number(),
  label: z.string(),
  effort: z.number().optional()
}) satisfies z.ZodType<DepartmentTaskType>;

export interface DepartmentCalendar {
  id: number;
  guid?: string;
  name?: string;
  calendarId: number;
  departmentId: number;
  startDate: string;
  endDate: string;
}

const DepartmentCalendarSchema = z
  .object({
    id: z.number(),
    calendarId: z.number().min(1, 'Обязательное поле'),
    departmentId: z.number(),
    startDate: z.string().nonempty('Укажите дату начала'),
    endDate: z.string().nonempty('Укажите дату окончания'),
    name: z.string().or(z.undefined()),
    guid: z.string().or(z.undefined())
  })
  .superRefine((schedule, ctx) => {
    if (!schedule.startDate || !schedule.endDate) return;

    const start = new Date(schedule.startDate);
    const end = new Date(schedule.endDate);

    if (start > end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Дата начала не может быть позже даты окончания',
        path: ['generalDatesErrors', 'startDate']
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Дата окончания не может быть ранее даты начала',
        path: ['generalDatesErrors', 'endDate']
      });
    }
  });

export type DetailedInformationFormType = z.infer<
  typeof DetailedInformationSchema
>;

export const DepartmentZonesSchema = LocationSchema.omit({
  latitude: true,
  longitude: true,
  excludedStreetsFias: true,
  excludedHousesFias: true
}).superRefine((data, ctx) => {
  validateDestinationArea(data, ctx); // Не передаем индекс
}) as z.ZodType<DepartmentZone>;

const ObjectForSelectionSchema: z.ZodSchema<ObjectForSelection> = z.object({
  id: z.number(),
  label: z.string()
});

const BaseSchema = z
  .object({
    parentKey: z.string().optional(),
    id: z.string().optional(),
    key: z.string().optional(),
    organization: z.string().max(40, namesMessage).optional(),
    priority: z.number().optional(),
    label: z
      .string()
      .nonempty({ message: 'Пожалуйста, укажите название' })
      .max(40, namesMessage)
      .refine((value) => value.trim().length > 0, {
        message: 'Поле не может содержать только пробелы'
      }),
    productivity: z
      .number()
      .or(z.string())
      .optional()
      .refine(
        (val) => {
          if (val === undefined || val === '') return true;
          const num = typeof val === 'string' ? parseFloat(val) : val;
          return !isNaN(num) && num >= 0;
        },
        { message: 'Производительность не может быть отрицательной' }
      ),
    userRole: z.array(RoleUISchema).optional(),
    taskTypes: z.array(TaskTypesSchema).optional(),

    data: z.string(),
    isCustomProductivity: z.boolean().optional(),
    intervalDuration: z
      .number()
      .optional()
      .refine(
        (val) => {
          if (val === undefined || val === null) return true;
          return Number.isInteger(val) && val >= 1 && val <= 8;
        },
        { message: 'Значение должно быть целым числом от 1 до 8' }
      ),
    isIntervalType: z.boolean().optional(),
    departmentCalendars: z.array(DepartmentCalendarSchema).optional(),
    departmentSchedules: z.array(ScheduleWithDatesSchema).optional(),
    // старое решение "Зоны обслуживания" (service areas)
    departmentZones: z.array(DepartmentZonesSchema).optional(),
    geographicalZones: z.array(ObjectForSelectionSchema).optional(),
    // Расписание распределения нарядов на сотрудников без оптимизации расстояния
    useEmployeeAssignmentSchedule: z.boolean().optional(),
    employeeAssignmentScheduleSetting: z
      .union([z.null(), EmployeeAssignmentScheduleSettingSchema])
      .optional()
  })
  .merge(
    LocationSchema.omit({
      excludedStreetsFias: true,
      excludedHousesFias: true
    })
  );

export const DetailedInformationSchema = BaseSchema.superRefine((data, ctx) => {
  validateFias(data, ctx);
  validateCustomAddress(data, ctx);
  validateCoordinates(data, ctx);

  if (data.departmentCalendars && data.departmentCalendars.length > 1) {
    validateDateRangesOverlap(
      data.departmentCalendars,
      ctx,
      'departmentCalendars',
      'Даты пересекаются с другим календарем'
    );
  }

  if (data.departmentSchedules && data.departmentSchedules.length > 1) {
    validateDateRangesOverlap(
      data.departmentSchedules,
      ctx,
      'departmentSchedules',
      'Даты пересекаются с другим расписанием'
    );
  }

  if (
    data.useEmployeeAssignmentSchedule &&
    !data.employeeAssignmentScheduleSetting
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'Заполните настройки расписания распределения нарядов на сотрудников',
      path: ['useEmployeeAssignmentSchedule']
    });
  }
});
